import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ExternalLink, Layout, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import Seo from "../../components/Seo";

const statusColor = { draft: "bg-slate-100 text-slate-600", published: "bg-indigo-100 text-indigo-800" };

export default function AdminPages() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const { data } = useQuery({ queryKey: ["admin-pages"], queryFn: async () => (await api.get("/admin/pages")).data });

  const create = async () => {
    if (!title) return toast.error("Enter a page title");
    try { const { data } = await api.post("/admin/pages", { title, status: "draft", sections: [] }); toast.success("Page created"); setOpen(false); setTitle(""); nav(`/admin/pages/${data.id}`); }
    catch { toast.error("Failed"); }
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/admin/pages/${p.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-pages"] }); };

  return (
    <div>
      <Seo title="Landing Pages | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Layout className="w-6 h-6 text-indigo-700" /> Landing Pages</h1><p className="text-slate-500">Build custom landing pages and add them to the menu.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-indigo-900 hover:bg-indigo-800" data-testid="add-page-btn"><Plus className="w-4 h-4 mr-2" /> New Page</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create Landing Page</DialogTitle></DialogHeader>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Page title (e.g. Invest in Faridabad)" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="page-title-input" />
            <DialogFooter><Button onClick={create} className="bg-indigo-900 hover:bg-indigo-800" data-testid="page-create">Create & Build</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="pages-table">
          <thead><tr className="border-b border-slate-100 text-left text-slate-500"><th className="p-4">Title</th><th className="p-4">URL</th><th className="p-4">Status</th><th className="p-4">In Menu</th><th className="p-4"></th></tr></thead>
          <tbody>
            {(data?.items || []).map((p) => (
              <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="p-4 font-medium text-slate-900">{p.title}</td>
                <td className="p-4 font-mono text-xs text-slate-500">/p/{p.slug}</td>
                <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${statusColor[p.status]}`}>{p.status}</span></td>
                <td className="p-4">{p.show_in_menu ? <Eye className="w-4 h-4 text-indigo-600" /> : <EyeOff className="w-4 h-4 text-slate-300" />}</td>
                <td className="p-4"><div className="flex gap-3 items-center">
                  {p.status === "published" && <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-700"><ExternalLink className="w-4 h-4" /></a>}
                  <button onClick={() => nav(`/admin/pages/${p.id}`)} className="text-indigo-800 text-xs font-semibold" data-testid={`edit-page-${p.id}`}>Edit</button>
                  <button onClick={() => del(p)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data?.items?.length && <div className="p-12 text-center text-slate-400">No landing pages yet. Create your first one.</div>}
      </div>
    </div>
  );
}
